import React, { Suspense } from 'react';
import tw from 'twin.macro';
import Cookies from 'js-cookie';
import { css } from '@emotion/react';
import { Trans } from '@lingui/macro';
import { motion } from 'framer-motion';
import { CountdownCircleTimer } from 'react-countdown-circle-timer';

import {
  AttendeeFragment,
  PublicSetWithEWebinarFragment,
  RoomType,
  useGetAttendeeLazyQuery,
} from '$/GraphQL/schema/schema';
import {
  InteractionDisplayState,
  InteractionInfo,
} from '$/components/Interactions/InteractionInfo';

import {
  staticWebinarDimensions,
  withWebinarPlayerContext,
} from '$/components/WebinarPlayer/context';
import { PlayerMode } from '$/components/WebinarPlayer/types';

import { logger } from '$/utils/logger';
import { langDirClassname } from '$/shared/frontend/utils/i18n';

import { ReactComponent as PlayButtonIcon } from '$/images/playButton.svg';
import { JOIN_IN_SECS } from './variables';

const VideoPreview = React.lazy(() => import('$/components/VideoPreview/VideoPreview'));

const renderTime = ({ remainingTime }: { remainingTime: number }): JSX.Element => {
  if (remainingTime === 0) {
    return <PlayButtonIcon className='play-button' />;
  }

  return <div className='text-white text-center font-mono text-2xl'>{remainingTime}</div>;
};

const NextWebinarTimerOverlay: React.FC<{
  attendee?: AttendeeFragment;
  set?: PublicSetWithEWebinarFragment;
}> = withWebinarPlayerContext(['playbackPositionValue', 'mode', 'rooms', 'ewebinar'])(({
  attendee,
  set,
  context: { rooms, playbackPositionValue, mode, ewebinar },
}) => {
  const [opened, setOpened] = React.useState(false);

  const openNextWebinar = React.useCallback(
    (e?: React.MouseEvent): void => {
      if (!attendee || !set) return;

      if (e) {
        // From mouse click
        e.preventDefault();
      } else {
        // From timer
        if (Cookies.get(`nextwebinar-opened-${set.id}-${attendee.id}`)) {
          // Don't auto open if already opened
          return;
        }
      }

      if (attendee.urls.join) {
        window.open(attendee.urls.join, '_blank');
        setOpened(true);
        Cookies.set(`nextwebinar-opened-${set.id}-${attendee.id}`, 'true', { expires: 1 }); // Expire in 1 day
      }
    },
    [attendee, set]
  );

  const initialTimerDuration = React.useMemo(() => {
    let bufferSecs = 15;
    if (mode === PlayerMode.replay) {
      bufferSecs = -1;
    }
    return Math.max(
      0,
      Math.min(
        JOIN_IN_SECS,
        rooms.getRoom(RoomType.Presentation).endsAt - playbackPositionValue.get() + bufferSecs
      )
    );
  }, [mode, playbackPositionValue, rooms]);

  if (!attendee || !set || !set.publicWebinar) return null;

  const lang = ewebinar.viewerRoomSettings.language ?? 'en';

  return (
    <motion.div
      id='NextWebinarTimer'
      dir={langDirClassname(lang)}
      className='flex scale-overlay flex-row items-center cursor-pointer text-white absolute z-player-next-webinar'
      css={css`
        transform-origin: bottom right;
        bottom: ${staticWebinarDimensions.videoControlsArea.bottomBar.height}px;
        right: 1%;

        .top-message {
          position: absolute;
          ${tw`rounded-t-lg text-sm font-semibold py-1 px-4`}
          top: 0;
          left: 0;
          right: 0;
          background: rgba(0, 0, 0, 0.4);
          backdrop-filter: blur(5px);
          z-index: 10;
        }

        .play-button {
          width: 60px;
          backdrop-filter: blur(10px);
          border-radius: 50%;

          circle {
            stroke: #ffffff00;
            fill: #000000;
            fill-opacity: 0.5;
          }
        }
      `}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      onClick={openNextWebinar}
    >
      {!Cookies.get(`nextwebinar-opened-${set.id}-${attendee.id}`) && !opened ? (
        <div className='top-message'>
          <Trans>Next webinar in</Trans>
        </div>
      ) : (
        <div className='top-message'>
          <Trans>Start next webinar</Trans>
        </div>
      )}
      <div className='w-18e h-10e rounded-lg overflow-hidden'>
        <Suspense>
          <VideoPreview
            setId={set.id}
            thumbnailWebinar={set.publicWebinar!}
            setSocialMediaTags={set.socialMediaTags}
            showImageOnly
            thumbnailOnly
          />
        </Suspense>
      </div>

      <div
        className='absolute flex justify-center items-center w-full'
        css={css`
          & > div {
            background-color: rgba(0, 0, 0, 0.7);
            border-radius: 50%;
          }
        `}
      >
        {!Cookies.get(`nextwebinar-opened-${set.id}-${attendee.id}`) ? (
          <CountdownCircleTimer
            isPlaying={!Cookies.get(`nextwebinar-opened-${set.id}-${attendee.id}`)}
            duration={initialTimerDuration}
            strokeWidth={4}
            trailStrokeWidth={4}
            trailColor='#ffffff00'
            strokeLinecap='square'
            size={60}
            rotation='clockwise'
            colors={['#fff', '#fff']}
            colorsTime={[initialTimerDuration, 0]}
            onComplete={(): void => {
              // Don't auto-open if I just loaded the replay at the end of the webinar
              initialTimerDuration !== 0 && openNextWebinar();
            }}
          >
            {renderTime}
          </CountdownCircleTimer>
        ) : (
          <PlayButtonIcon className='play-button' />
        )}
      </div>
    </motion.div>
  );
});

export const NextWebinarOverlay: React.FC<{
  interaction: InteractionInfo;
}> = withWebinarPlayerContext(['playbackPosition', 'interactionState', 'mode'])(({
  interaction,
  context: { playbackPosition, interactionState, mode },
}) => {
  const [getAttendeeQuery, getAttendeeQueryResponse] = useGetAttendeeLazyQuery();
  const { reaction } = interaction;

  React.useEffect(() => {
    if (
      !reaction?.detailsFields?.nextWebinar?.attendeeId ||
      !reaction?.detailsFields?.nextWebinar.setId
    ) {
      return;
    }

    getAttendeeQuery({
      variables: {
        id: reaction.detailsFields.nextWebinar.attendeeId,
        setId: reaction.detailsFields.nextWebinar.setId,
      },
    });
  }, [
    getAttendeeQuery,
    reaction?.detailsFields?.nextWebinar?.attendeeId,
    reaction?.detailsFields?.nextWebinar?.setId,
  ]);

  const attendeeInNextWebinar = getAttendeeQueryResponse.data?.attendee;
  const nextWebinar = interactionState[interaction.shortId]?.set;

  if (!nextWebinar) {
    logger.error(`Next webinar card doesn't reference a published webinar`);
    return null;
  }

  const displayState = interaction.cardDisplayState(playbackPosition, mode);
  if (displayState !== InteractionDisplayState.Result) {
    return null;
  }

  return <NextWebinarTimerOverlay attendee={attendeeInNextWebinar} set={nextWebinar} />;
});
